/**
 * Hook: usePublishedContractRequiredDocs
 * Gerencia documentos exigidos por contrato publicado
 * 
 * @version 1.0.0
 * RELEASE: 2026-04-09
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import { showToast } from '@/lib/toast';

const logger = createLogger('PublishedContractRequiredDocs');

/** Documento exigido por contrato publicado */
export interface RequiredDocument {
  id: string;
  published_contract_id: string;
  document_type_id: string;
  tenant_id: string;
  is_required: boolean;
  sort_order: number;
  created_at: string;
  /** Join com document_types */
  document_types?: {
    id: string;
    name: string;
    category: string;
    description: string | null;
  };
}

/**
 * Hook para gerenciar documentos exigidos de um contrato publicado
 */
export function usePublishedContractRequiredDocs(publishedContractId?: string) {
  const { currentTenant } = useAuth();
  const queryClient = useQueryClient();
  const tenantId = currentTenant?.id;

  // Listagem dos documentos exigidos
  const { data: requiredDocs = [], isLoading } = useQuery({
    queryKey: ['published-contract-required-docs', publishedContractId],
    queryFn: async (): Promise<RequiredDocument[]> => {
      if (!publishedContractId || !tenantId) return [];

      const { data, error } = await supabase
        .from('published_contract_required_documents')
        .select(`
          *,
          document_types (id, name, category, description)
        `)
        .eq('published_contract_id', publishedContractId)
        .order('sort_order', { ascending: true });

      if (error) {
        logger.error('Erro ao buscar docs exigidos:', error.message);
        throw error;
      }

      return data as RequiredDocument[];
    },
    enabled: !!publishedContractId && !!tenantId,
  });

  // Adicionar documento exigido
  const addRequiredDoc = useMutation({
    mutationFn: async (input: { document_type_id: string; is_required: boolean }) => {
      if (!publishedContractId || !tenantId) throw new Error('IDs ausentes');

      const { data, error } = await supabase
        .from('published_contract_required_documents')
        .insert({
          published_contract_id: publishedContractId,
          document_type_id: input.document_type_id,
          tenant_id: tenantId,
          is_required: input.is_required,
          sort_order: requiredDocs.length,
        })
        .select(`*, document_types (id, name, category, description)`)
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['published-contract-required-docs', publishedContractId] });
    },
    onError: (error: Error) => {
      showToast.error('Erro ao adicionar documento', error.message);
    },
  });

  // Atualizar (ex: toggle obrigatório)
  const updateRequiredDoc = useMutation({
    mutationFn: async (input: { id: string; is_required: boolean }) => {
      const { error } = await supabase
        .from('published_contract_required_documents')
        .update({ is_required: input.is_required })
        .eq('id', input.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['published-contract-required-docs', publishedContractId] });
    },
  });

  // Remover
  const removeRequiredDoc = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('published_contract_required_documents')
        .delete()
        .eq('id', id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['published-contract-required-docs', publishedContractId] });
    },
  });

  return {
    requiredDocs,
    isLoading,
    addRequiredDoc,
    updateRequiredDoc,
    removeRequiredDoc,
  };
}
